/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import ajax from "helpers/ajax";
import {Action, createAction} from "helpers/createAction";
import {Activity} from "types/entities/Activity";
import {Package} from "types/entities/Package";
import {
    BookingReducerPushReservationNoteAction,
    BookingReducerPushUpsellAction,
    BookingReducerResetReservationNoteAction,
    BookingReducerSetActivitiesAction,
    BookingReducerSetCustomerAction,
    BookingReducerSetDateChosenByCustomerAction,
    BookingReducerSetFilterAction,
    BookingReducerSetFilterDrivesAdultCountAction,
    BookingReducerSetFilterDrivesChildCountAction,
    BookingReducerSetPackageAction,
    BookingReducerSetPackageCartAction,
    BookingReducerSetPackagesAction,
    BookingReducerSetTimeslotsAction,
    BookingReducerSetTimeslotsAllowedAction,
    BookingReducerSetUpsellAction,
} from "store/booking/BookingReducer";
import {BookingActivitiesResponse, BookingPackagesResponse, BookingPackagesTimeslotsResponse} from "types/responses";
import {ThunkAction} from "model/store/thunk";
import {BookingFilter, BookingTimeslot} from "types/app";
import {PackageItem, PackageItemItemType} from "types/entities/PackageItem";
import {Company} from "types/entities/Company";
import {PackagesCart} from "types/entities/PackagesCart";
import {Payment} from "types/entities/Payment";
import {DEFAULT_BASENAME, DEFAULT_HALL_ID, HAS_BASENAME} from "helpers/config";
import Dates from "utils/Dates";
import {BookingInitCartResponse} from "types/responses/BookingInitCartResponse";
import {Timeslot} from "types/entities/Timeslot";
import {VerificationType} from "types/enums/VerificationType";
import {CompanyInputs} from "types/entities/specific/CompanyInputs";
import VoucherAction from "store/shop/VoucherAction";
import {AccountDriver} from "types/entities/AccountDriver";
import {consoleLog} from "utils/utility";
import SessionAction from "store/session/SessionAction";
import {PackageCategory} from "types/enums/PackageCategory";
import {PackagesItemByPerson, PackagesItemsResponse} from "types/entities/PackagesItems";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {PackagesCartItemVoucher} from "types/entities/PackagesCartItemVoucher";

export type BookingPackagesInput = {
    date: string;
    adults: number;
    children: number;
    activity_ids: number[];
};

export type PackagesTimeSlots = {
    package_id: number;
    date: string;
    adults: number;
    children: number;
    upsell: PackageUpsellItem[];
    hours_count?: number;
};

export type CategoryTimeSlots = {
    package_category_name: PackageCategory;
    date: string;
    hall_id: number;
    adults: number;
    children: number;
    activity_ids: number[];
    timeslot_id: number | null;
};

export type BookPackageParams = {
    package_id: number;
    adults: number;
    children: number;
    upsell: PackageUpsellItem[];
};
export type BookPackageUpsellParams = {
    timeslot_id: number | null;
    package_id: number;
    adults: number;
    children: number;
    upsell: PackageUpsellItem[];
};

export type PackageUpsellItem = {package_item_id: number; upsell_option: number; quantity: number};

export type BookTimeslotParams = {
    package_id: number;
    timeslot_id: number | null;
    adults: number;
    children: number;
    upsell: PackageUpsellItem[];
    hours_count?: number;
};

export type BookPackageItemsParams = {
    timeslot_id: number | null;
    package_item_ids: any[];
    package_category_name: string;
    upsell: PackageUpsellItem[];
    adults: number;
    children: number;
    activity_ids: number[];
    seller_id?: number;
};

export type BookCustomTimeslotParams = {
    package_id: number;
    timeslot_ids: number[];
    adults: number;
    children: number;
    upsell: PackageUpsellItem[];
    hours_count?: number;
};

export type SetCustomerParams = {
    name: string;
    first_name: string;
    last_name: string;
    email: string;
    city: string;
    phone: string;
    street_address: string;
    postal_code: string;
};

export type ReservationNote = {
    id: number;
    guid: string;
    reservation_id: number;
    note: string;
    creator_id: number | null;
    created_at: string;
    updated_at: string;
};

export type PackageUpsell = {
    packageItem: PackageItem;
    upsell_option: number;
    count: number;
};

export enum SaferPayErrorNames {
    ActionNotSupported = "ACTION_NOT_SUPPORTED", // The requested action is not supported in the given context or the action can't be executed with the request data.
    AliasInvalid = "ALIAS_INVALID", // The alias is not known or already used (in case of registration). Solution: Use another alias for registration
    AmountInvalid = "AMOUNT_INVALID", // The amount does not adhere to the restrictions for this action. E.g. it might be exceeding the allowed capture amount.
    AuthenticationFailed = "AUTHENTICATION_FAILED", // Wrong password, wrong client certificate, invalid token, wrong HMAC. Solution: Use proper credentials, fix HMAC calculation, use valid token
    BlockedByRiskManagement = "BLOCKED_BY_RISK_MANAGEMENT", // Action blocked by risk management. Solution: Unblock in Saferpay Risk Management (Backoffice)
    CardCheckFailed = "CARD_CHECK_FAILED", // Invalid card number or cvc (this is only returned for the SIX-internal chard check feature for Alias/InsertDirect). Solution: Let the card holder correct the entered data
    CardCvcInvalid = "CARD_CVC_INVALID", // Wrong cvc entered. Solution: Retry with correct cvc
    CardCvcRequired = "CARD_CVC_REQUIRED", // Cvc not entered but required. Solution: Retry with cvc entered
    CommunicationFailed = "COMMUNICATION_FAILED", // The communication to the processor failed. Solution: Try again or use another means of payment
    CommunicationTimeout = "COMMUNICATION_TIMEOUT", // Saferpay did not receive a response from the external system in time. It’s possible that an authorization was created, but Saferpay is not able to know this. Solution: Check with the acquirer if there is an authorization which needs to be canceled.
    ConditionNotSatisfied = "CONDITION_NOT_SATISFIED", // The condition which was defined in the request could not be satisfied.
    CurrencyInvalid = "CURRENCY_INVALID", // Currency does not match referenced transaction currency.
    GeneralDeclined = "GENERAL_DECLINED", // Transaction declined by unknown reason
    InternalError = "INTERNAL_ERROR", // Internal error in Saferpay. Solution: Try again
    NoContract = "NO_CONTRACT", // No contract available for the brand / currency combination. Solution: Use another card or change the currency to match an existing contract or have the currency activated for the contract.
    NoCreditsAvailable = "NO_CREDITS_AVAILABLE", // No more credits available for this account. Solution: Buy more transaction credits
    PayerAuthenticationRequired = "PAYER_AUTHENTICATION_REQUIRED", // Payer authentication required to proceed (soft decline).
    PaymentMeansInvalid = "PAYMENTMEANS_INVALID", // Invalid means of payment (e.g. invalid card)
    PaymentMeansNotSupported = "PAYMENTMEANS_NOT_SUPPORTED", // Unsupported means of payment (e.g. non SEPA IBAN)
    PermissionDenied = "PERMISSION_DENIED", // No permission (e.g. terminal does not belong to the customer)
    ThreesAuthenticationFailed = "3DS_AUTHENTICATION_FAILED", // 3D-secure authentication failed – the transaction must be aborted. Solution: Use another card or means of payment
    TokenExpired = "TOKEN_EXPIRED", // The token is expired. Solution: Create a new token.
    TokenInvalid = "TOKEN_INVALID", // The token either does not exist for this customer or was already used
    TransactionAborted = "TRANSACTION_ABORTED", // The transaction was aborted by the payer.
    TransactionAlreadyCaptured = "TRANSACTION_ALREADY_CAPTURED", // Transaction already captured.
    TransactionDeclined = "TRANSACTION_DECLINED", // Declined by the processor. Solution: Use another card or check details.
    TransactionInWrongState = "TRANSACTION_IN_WRONG_STATE",
    TransactionNotFound = "TRANSACTION_NOT_FOUND",
    TransactionNotStarted = "TRANSACTION_NOT_STARTED", // The transaction was not started by the payer. Therefore, no final result for the transaction is available. Solution: Try again later.
    UnexpectedErrorByAcquirer = "UNEXPECTED_ERROR_BY_ACQUIRER", // The acquirer returned an unexpected error code. Solution: Try again
    UpdateCardInformation = "UPDATE_CARD_INFORMATION", // Card details need to be updated in order to have the possibility of a successful payment. Solution: Update card data
    ValidationFailed = "VALIDATION_FAILED", // Validation failed. Solution: Fix request
}

export interface PaymentAssert {
    ResponseHeader: {
        SpecVersion: string;
        RequestId: string;
    };
    Behavior: string;
    ErrorName: SaferPayErrorNames;
    ErrorMessage: string;
    TransactionId: string;
}

class BookingAction {
    static readonly SET_ACTIVITIES = "BookingAction.SetActivities";
    static readonly SET_PACKAGES = "BookingAction.SetPackages";
    static readonly SET_TIMESLOTS = "BookingAction.SetTimeslots";
    static readonly SET_TIMESLOTS_ALLOWED = "BookingAction.SetTimeslotsAllowed";
    static readonly SET_PACKAGE_CART = "BookingAction.SetPackageCart";
    static readonly SET_CUSTOM_TIMESLOT = "BookingAction.SetCustomTimeslot";

    static readonly SET_POSSIBLE_TIMESLOTS = "BookingAction.SetPossibleTimeslots";
    static readonly RESET_CUSTOM_TIMESLOTS = "BookingAction.ResetCustomTimeslots";
    static readonly SET_EMAIL_VERIFIED = "BookingAction.SetEmailVerified";
    static readonly SET_DATE_CHOSEN_BY_CUSTOMER = "BookingAction.SetDateChosenByCustomer";
    static readonly SET_FILTER = "BookingAction.SetFilter";
    static readonly SET_FILTER_ADULTS_COUNT = "BookingAction.SetFilterAdultsCount";
    static readonly SET_FILTER_CHILDREN_COUNT = "BookingAction.SetFilterChildrenCount";
    static readonly SET_FILTER_ACTIVITIES = "BookingAction.SetFilterActivities";
    static readonly SET_PACKAGE = "BookingAction.SetPackage";
    static readonly PUSH_RESERVATION_NOTE = "BookingAction.PushReservationNote";
    static readonly RESET_RESERVATION_NOTE = "BookingAction.ResetReservationNote";

    static readonly SET_UPSELL = "BookingAction.SetUpsell";
    static readonly PUSH_UPSELL = "BookingAction.PushUpsell";
    static readonly REMOVE_UPSELL = "BookingAction.RemoveUpsell";
    static readonly CLEAR_UPSELLS = "BookingAction.ClearUpsells";
    static readonly SET_CUSTOMER = "BookingAction.SetCustomer";
    static readonly SET_COOKIES = "BookingAction.SetCookies";
    static readonly SET_PACKAGES_CART_PAYMENT = "BookingAction.SetPackagesCartPayment";
    static readonly SET_PACKAGES_CART_PAYMENT_ASSERT = "BookingAction.SetPackagesCartPaymentAssert";

    static readonly SET_CURRENT_NAVIGATION_STEP = "BookingAction.SetCurrentNavigationStep";
    static readonly CLEAR_BOOKING_PROCESS_STATE = "BookingAction.ClearBookingProcessState";

    static readonly SET_SELECTED_TIMESLOT = "BookingAction.SetSelectedTimeslot";
    static readonly SET_AVAILABLE_PACKAGES_ITEMS = "BookingAction.AvailablePackageItems";
    static readonly SET_SELECTED_PACKAGE_ITEMS = "BookingAction.SetSelectedPackageItems";
    static readonly SET_SELECTED_PACKAGE_ITEM = "BookingAction.SetSelectedPackageItem";
    static readonly RESET_SELECTED_PACKAGE_ITEMS = "BookingAction.ResetSelectedPackageItems";

    static fetchReservationsForDate = (date: string, seller_id: number) => {
        return dispatch => {
            return ajax
                .get("/online-venue/reservation/seller/list-for-date", {
                    params: {
                        seller_id: seller_id,
                        date: date,
                    },
                })
                .then((r: BookingActivitiesResponse) => {
                    return r;
                });
        };
    };

    static fetchReservationOffer = (reservation_id: number): ((dispatch) => Promise<any>) => {
        return async dispatch => {
            const requestProps: {
                document_type: string;
                is_download?: boolean;
            } = {
                document_type: "offer",
                is_download: true,
            };

            try {
                const response: {
                    offer: string;
                } = await ajax.post("/online-venue/reservation/seller/get_reservation_offer/" + reservation_id, requestProps);
                if (response?.offer) {
                    return Promise.resolve(response);
                }
                return Promise.reject(response);
            } catch (error) {
                console.error(error);
                return Promise.reject(error);
            }
        };
    };

    static fetchSetReservationLeadOrigin = (reservation_id: number, lead_origin: string) => {
        return dispatch => {
            return ajax
                .post("/online-venue/reservation/seller/lead_origin/" + reservation_id, {
                    lead_origin: lead_origin,
                })
                .then((r: BookingActivitiesResponse) => {
                    return r;
                });
        };
    };

    static fetchActivities = (): ThunkAction<Promise<BookingActivitiesResponse>> => {
        return dispatch => {
            return ajax
                .get("/booking/activities", {
                    params: {
                        hall_id: DEFAULT_HALL_ID,
                    },
                })
                .then((r: BookingActivitiesResponse) => {
                    dispatch<BookingReducerSetActivitiesAction>(BookingAction.setActivities(r.activities));
                    return r;
                });
        };
    };

    static fetchSendSecurityCodeForLogin = (email: string): ((dispatch) => Promise<AccountDriver>) => {
        return async dispatch => {
            try {
                const response = await ajax.post("/online-venue/reservation/send-login-with-code", {
                    identifier: email,
                    api_key: "777f80c6-6d00-11ee-843d-88d7f67de6c2", // @TODO: set api key
                });
                if (response?.success) {
                    return Promise.resolve(response);
                } else {
                    return Promise.reject("login_not_found");
                }
            } catch (err) {
                consoleLog("fetchSendSecurityCodeForLogin - reject");
                consoleLog(err);
                return Promise.reject("login_not_found");
            }
        };
    };

    static fetchChangeUpsellPriceAndQuantity = (reservation_id: number, packagesCartItem: PackagesCartItem, type: string, value: string | number) => {
        return async dispatch => {
            const url =
                packagesCartItem.packageItem?.type === PackageItemItemType.Upsell
                    ? "/online-venue/reservation/seller/change_upsell/"
                    : "/online-venue/reservation/seller/change_item/";
            try {
                const response = await ajax.post(url + reservation_id, {
                    item_id: packagesCartItem.id,
                    packages_cart_id: packagesCartItem.packages_cart_id,
                    package_item_id: packagesCartItem.package_item_id,
                    type: type,
                    value: value,
                    api_key: "777f80c6-6d00-11ee-843d-88d7f67de6c2", // @TODO: set api key
                });
                if (response?.success) {
                    return Promise.resolve(response);
                } else {
                    return Promise.reject("upsell_change_failed");
                }
            } catch (err) {
                consoleLog("upsell_change_failed - reject");
                consoleLog(err);
                return Promise.reject("upsell_change_failed");
            }
        };
    };

    static fetchChangeVoucherPrice = (reservation_id: number, packagesCartItemVoucher: PackagesCartItemVoucher, type: string, value: string | number) => {
        return async dispatch => {
            const url = "/online-venue/reservation/seller/change_voucher/";
            try {
                const response = await ajax.post(url + reservation_id, {
                    packages_cart_item_voucher_id: packagesCartItemVoucher.id,
                    packages_cart_id: packagesCartItemVoucher.packages_cart_id,
                    type: type,
                    value: value,
                    api_key: "777f80c6-6d00-11ee-843d-88d7f67de6c2", // @TODO: set api key
                });
                if (response?.success) {
                    return Promise.resolve(response);
                } else {
                    return Promise.reject("voucher_change_failed");
                }
            } catch (err) {
                consoleLog("voucher_change_failed - reject");
                consoleLog(err);
                return Promise.reject("voucher_change_failed");
            }
        };
    };

    static fetchDeleteUpsell = (reservation_id: number, packagesCartItem: PackagesCartItem) => {
        return async dispatch => {
            try {
                const response = await ajax.delete("/online-venue/reservation/seller/delete_upsell/" + reservation_id, {
                    data: {
                        item_id: packagesCartItem.id,
                        packages_cart_id: packagesCartItem.packages_cart_id,
                        package_item_id: packagesCartItem.package_item_id,
                        api_key: "777f80c6-6d00-11ee-843d-88d7f67de6c2", // @TODO: set api key
                    },
                });
                if (response?.success) {
                    return Promise.resolve(response);
                } else {
                    return Promise.reject("upsell_delete_failed");
                }
            } catch (err) {
                consoleLog("upsell_delete_failed - reject");
                consoleLog(err);
                return Promise.reject("upsell_delete_failed");
            }
        };
    };

    static fetchDeleteVoucher = (reservation_id: number, voucher_id: number) => {
        return async dispatch => {
            try {
                const response = await ajax.delete("/online-venue/reservation/seller/delete_voucher/" + reservation_id, {
                    data: {
                        voucher_id: voucher_id,
                        api_key: "777f80c6-6d00-11ee-843d-88d7f67de6c2", // @TODO: set api key
                    },
                });
                if (response?.success) {
                    return Promise.resolve(response);
                } else {
                    return Promise.reject("voucher_delete_failed");
                }
            } catch (err) {
                consoleLog("voucher_delete_failed - reject");
                consoleLog(err);
                return Promise.reject("voucher_delete_failed");
            }
        };
    };

    static fetchSendSellerSecurityCodeForLogin = (email: string): ((dispatch) => Promise<AccountDriver>) => {
        return async dispatch => {
            try {
                const response = await ajax.post("/online-venue/reservation/seller/send-login-with-code", {
                    identifier: email,
                    api_key: "777f80c6-6d00-11ee-843d-88d7f67de6c2", // @TODO: set api key
                });
                if (response?.success) {
                    return Promise.resolve(response);
                } else {
                    return Promise.reject("login_not_found");
                }
            } catch (err) {
                consoleLog("fetchSendSecurityCodeForLogin - reject");
                consoleLog(err);
                return Promise.reject("login_not_found");
            }
        };
    };

    static fetchGetCompanyFromAutocomplete = (name: string): ((dispatch) => Promise<Company[]>) => {
        return async dispatch => {
            try {
                const response = await ajax.post("/online-venue/reservation/seller/company-autocomplete", {
                    identifier: name,
                    api_key: "777f80c6-6d00-11ee-843d-88d7f67de6c2", // @TODO: set api key
                });
                if (response?.success) {
                    return Promise.resolve(response.companies);
                } else {
                    return Promise.reject("company_not_found");
                }
            } catch (err) {
                consoleLog("fetchGetCompanyFromAutocomplete - reject");
                consoleLog(err);
                return Promise.reject("company_not_found");
            }
        };
    };
    static fetchGetLoginWithCode = (email: string, code: string): ((dispatch) => Promise<AccountDriver>) => {
        return async dispatch => {
            try {
                const response: {
                    user: AccountDriver;
                    reason?: string;
                    access_token: string;
                    token_type: string;
                    expires_at: number;
                    uid: number;
                    home_hall_id: number;
                    watchdogWorking: boolean;
                } = await ajax.post("/online-venue/reservation/login-with-code", {
                    identifier: email,
                    code: code,
                    api_key: "777f80c6-6d00-11ee-843d-88d7f67de6c2", // @TODO: set api key
                });
                if (response.user) {
                    dispatch(SessionAction.setSession(response));
                    return Promise.resolve(response.user);
                } else {
                    consoleLog("fetchGetLoginWithCode - REJECT");
                    return Promise.reject(response.reason);
                }
            } catch (err) {
                return Promise.reject(err);
            }
        };
    };

    static fetchGetSellerLoginWithCode = (email: string, code: string): ((dispatch) => Promise<AccountDriver>) => {
        return async dispatch => {
            try {
                const response: {
                    user: AccountDriver;
                    reason?: string;
                    access_token: string;
                    token_type: string;
                    expires_at: number;
                    uid: number;
                    home_hall_id: number;
                    watchdogWorking: boolean;
                } = await ajax.post("/online-venue/reservation/seller/login-with-code", {
                    identifier: email,
                    code: code,
                    api_key: "777f80c6-6d00-11ee-843d-88d7f67de6c2", // @TODO: set api key
                });
                if (response.user) {
                    dispatch(SessionAction.setSession(response));
                    return Promise.resolve(response.user);
                } else {
                    consoleLog("fetchGetLoginWithCode - REJECT");
                    return Promise.reject(response.reason);
                }
            } catch (err) {
                return Promise.reject(err);
            }
        };
    };

    static fetchPackages = (data: BookingPackagesInput, dateChosen: boolean): ThunkAction<Promise<BookingPackagesResponse>> => {
        return dispatch => {
            return ajax
                .get("/booking/packages", {
                    params: {
                        hall_id: DEFAULT_HALL_ID,
                        ...data,
                        respectDaysLimit: dateChosen ?? false,
                        test_site: false,
                    },
                })
                .then((r: BookingPackagesResponse) => {
                    dispatch<BookingReducerSetPackagesAction>(BookingAction.setPackages(r.packages));
                    dispatch<BookingReducerSetTimeslotsAllowedAction>(BookingAction.setTimeslotsAllowed(r.timeslotsAllowed));
                    return r;
                });
        };
    };

    static fetchAllPackages = (date: string): ThunkAction<Promise<BookingPackagesResponse>> => {
        return dispatch => {
            return ajax
                .get("/booking/packages", {
                    params: {
                        hall_id: DEFAULT_HALL_ID,
                        date: date,
                        all_items: true,
                    },
                })
                .then((r: BookingPackagesResponse) => {
                    dispatch<BookingReducerSetPackagesAction>(BookingAction.setPackages(r.packages));
                    dispatch<BookingReducerSetTimeslotsAllowedAction>(BookingAction.setTimeslotsAllowed(r.timeslotsAllowed));
                    return r;
                });
        };
    };

    static fetchPackagesByLocationParams = (location, filter, dateChosen: boolean = false, voucherCodes: string[] = []) => {
        return dispatch => {
            const queryParams = new URLSearchParams(location.search);
            const filterParams: any = Object.fromEntries(queryParams.entries());

            if (!filterParams) {
                return;
            }

            filterParams.activity_ids = filterParams.activity_ids?.split(",") || filter.activity_ids || [];
            filterParams.adults = +filterParams.adults || filter.adults || 0;
            filterParams.children = +filterParams.children || filter.children || 0;
            filterParams.date = filterParams.date || filter.date || Dates.ymd(new Date());
            filterParams.vouchers = voucherCodes;
            return dispatch(BookingAction.fetchPackages(filterParams, dateChosen));
        };
    };

    static fetchPackagesForHall = date => {
        return dispatch => {
            return dispatch(BookingAction.fetchAllPackages(date));
        };
    };

    static fetchPackagesTimeSlots = (data: PackagesTimeSlots): ThunkAction<Promise<BookingPackagesTimeslotsResponse>> => {
        return dispatch => {
            return ajax
                .get("/booking/timeslots", {
                    params: {
                        ...data,
                    },
                })
                .then((r: {bookingTimeslots: BookingTimeslot[]}) => {
                    //console.log(r);
                    dispatch<BookingReducerSetTimeslotsAction>(BookingAction.setTimeslots(r.bookingTimeslots));
                    return r;
                });
        };
    };

    ///api/booking/package-items?activity_ids[]=1&adults=2&children=0&date=2024-10-23&hall_id=1&package_category_name=general
    static fetchPackagesByCategoryAndTimeSlot = (data: CategoryTimeSlots, seller_id?: number): ThunkAction<Promise<any>> => {
        return dispatch => {
            return ajax
                .get("/booking/package-items", {
                    params: {
                        ...data,
                        seller_id,
                    },
                })
                .then((r: PackagesItemsResponse) => {
                    if (r?.packageItems) {
                        dispatch(BookingAction.setAvailablePackagesItems(r));
                        return Promise.resolve(r);
                    }
                    return Promise.reject(r);
                })
                .catch(err => {
                    return Promise.reject(err);
                });
        };
    };
    static fetchPackagesByCategoryAndTimeSlotForSeller = (data: CategoryTimeSlots, seller_id?: number): ThunkAction<Promise<any>> => {
        return dispatch => {
            return ajax
                .get("/booking/package-items-seller", {
                    params: {
                        ...data,
                        seller_id,
                    },
                })
                .then((r: PackagesItemsResponse) => {
                    if (r?.packageItems) {
                        dispatch(BookingAction.setAvailablePackagesItems(r));
                        return Promise.resolve(r);
                    }
                    return Promise.reject(r);
                })
                .catch(err => {
                    return Promise.reject(err);
                });
        };
    };

    static fetchCategoryTimeSlots = (data: CategoryTimeSlots, seller_id?: number): ThunkAction<Promise<BookingPackagesTimeslotsResponse>> => {
        return dispatch => {
            return ajax
                .get("/booking/start-times", {
                    params: {
                        ...data,
                        seller_id,
                    },
                })
                .then((r: {timeslots: BookingTimeslot[]}) => {
                    dispatch<BookingReducerSetTimeslotsAction>(BookingAction.setTimeslots(r.timeslots));
                    return r;
                });
        };
    };

    // static fetchBookPackage = (data: BookPackageParams): ThunkAction<Promise<BookingPackagesTimeslotsResponse>> => {
    //     return dispatch => {
    //         return ajax.post("/booking/book/package", data).then((r: {packageCart: PackagesCart}) => {
    //             dispatch(BookingAction.setPackageCart(r.packageCart));
    //             return r;
    //         });
    //     };
    // };

    static fetchBookPackageWithUpsell = (
        data: BookPackageUpsellParams,
        packagesCart: PackagesCart
    ): ((dispatch) => Promise<{
        packageCart: PackagesCart;
    }>) => {
        return dispatch => {
            return ajax.post("/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/book/package", data).then((r: {packageCart: PackagesCart}) => {
                dispatch(BookingAction.setPackageCart(r.packageCart));
                return r;
            });
        };
    };

    static fetchBookPackage = (
        data: BookTimeslotParams,
        packagesCart: PackagesCart,
        origin = "BookingAction"
    ): ThunkAction<Promise<BookingPackagesTimeslotsResponse>> => {
        return dispatch => {
            return ajax
                .post("/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/book/package", data)
                .then((r: {packageCart: PackagesCart; timeslot: Timeslot}) => {
                    dispatch<BookingReducerSetPackageCartAction>(BookingAction.setPackageCart(r.packageCart));
                    return r;
                });
        };
    };

    static fetchBookPackageNew = (
        data: BookPackageItemsParams,
        packagesCart: PackagesCart,
        origin = "BookingAction"
    ): ThunkAction<Promise<BookingPackagesTimeslotsResponse>> => {
        return dispatch => {
            return ajax
                .post("/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/book/package-items", data)
                .then((r: {packageCart: PackagesCart; timeslot: Timeslot}) => {
                    dispatch<BookingReducerSetPackageCartAction>(BookingAction.setPackageCart(r.packageCart));
                    return r;
                });
        };
    };

    static fetchBookPackageWithCustomTimeslots = (
        data: BookCustomTimeslotParams,
        packagesCart: PackagesCart
    ): ThunkAction<Promise<BookingPackagesTimeslotsResponse>> => {
        return dispatch => {
            return ajax
                .post("/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/book/package-custom-slots", data)
                .then((r: {packageCart: PackagesCart; timeslot: Timeslot}) => {
                    dispatch<BookingReducerSetPackageCartAction>(BookingAction.setPackageCart(r.packageCart));
                    return r;
                });
        };
    };

    static initCart = (withCleanup: boolean = true): ThunkAction<Promise<BookingInitCartResponse>> => {
        return dispatch => {
            return ajax.post("/booking/cart/create", {hall_id: DEFAULT_HALL_ID}).then(r => {
                dispatch(BookingAction.setEmailVerified(false));

                if (withCleanup) {
                    dispatch(VoucherAction.resetVouchers());
                    dispatch(BookingAction.setDateChosenByCustomer(false));
                }

                dispatch<BookingReducerSetPackageCartAction>(BookingAction.setPackageCart(r.packageCart));
                dispatch<Action<string, Payment | null>>(BookingAction.setPackagesCartPayment(null));
                dispatch<Action<string, PaymentAssert | null>>(BookingAction.setPackagesCartPaymentAssert(null));
                // dispatch(setSession(r.session));
                return r;
            });
        };
    };

    static initSellerCart = (withCleanup: boolean = true, seller_id: number): ThunkAction<Promise<BookingInitCartResponse>> => {
        return dispatch => {
            return ajax.post("/booking/cart/create", {hall_id: DEFAULT_HALL_ID, seller_id: seller_id}).then(r => {
                dispatch(BookingAction.setEmailVerified(false));

                if (withCleanup) {
                    dispatch(VoucherAction.resetVouchers());
                    dispatch(BookingAction.setDateChosenByCustomer(false));
                }

                dispatch<BookingReducerSetPackageCartAction>(BookingAction.setPackageCart(r.packageCart));
                dispatch<Action<string, Payment | null>>(BookingAction.setPackagesCartPayment(null));
                dispatch<Action<string, PaymentAssert | null>>(BookingAction.setPackagesCartPaymentAssert(null));
                // dispatch(setSession(r.session));
                return r;
            });
        };
    };

    static sendReservationNote = (packagesCart: PackagesCart, note: string): ThunkAction<Promise<BookingInitCartResponse>> => {
        return (dispatch, getState): Promise<any> => {
            const session = getState().sessionState.session;
            return ajax
                .post(
                    "/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/note",
                    {note: note},
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                )
                .then(r => {
                    return r;
                });
        };
    };

    static resetReservationNote = (): BookingReducerResetReservationNoteAction => {
        return createAction(BookingAction.RESET_RESERVATION_NOTE, null);
    };

    static pushReservationNote = (data: ReservationNote): BookingReducerPushReservationNoteAction => {
        return createAction(BookingAction.PUSH_RESERVATION_NOTE, data);
    };

    static fetchPackageCart = (packagesCart: PackagesCart): ThunkAction<Promise<BookingPackagesTimeslotsResponse>> => {
        return dispatch => {
            return ajax
                .get("/booking/cart/" + packagesCart.id + "/" + packagesCart.token, {params: {hall_id: DEFAULT_HALL_ID}})
                .then((r: {packageCart: PackagesCart}) => {
                    dispatch<BookingReducerSetPackageCartAction>(BookingAction.setPackageCart(r.packageCart));
                    return r;
                });
        };
    };

    static fetchVerification = (
        data: {value: string; hall_id: number; type: VerificationType},
        packagesCart: PackagesCart
    ): ((dispatch) => Promise<{
        verification_method: string;
        verification_request_id: string;
        status: number;
    }>) => {
        return async dispatch => {
            const r = await ajax.post("/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/verify", data);
            return r.verification_status;
        };
    };

    static fetchVerificationForShop = (data: {
        value: string;
        hall_id: number;
        type: VerificationType;
    }): ((dispatch) => Promise<{
        verification_method: string;
        verification_request_id: string;
        status: number;
    }>) => {
        return async dispatch => {
            const r = await ajax.post("/shop/verify", data);
            return r.verification_status;
        };
    };

    static fetchSubmitVerificationCode = (
        data: {verification_code: string; request_id: string; type: VerificationType},
        packagesCart: PackagesCart
    ): ((dispatch) => Promise<{result: boolean; verification_request_id: string}>) => {
        return async dispatch => {
            const r = await ajax.post("/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/submit-verification-code", data);
            return r;
        };
    };

    static fetchSubmitVerificationCodeForShop = (data: {
        verification_code: string;
        request_id: string;
        type: VerificationType;
    }): ((dispatch) => Promise<{result: boolean; verification_request_id: string}>) => {
        return async dispatch => {
            const r = await ajax.post("/shop/submit-verification-code", data);
            return r;
        };
    };

    static fetchEditCustomer = (
        data: Company
    ): ((dispatch) => Promise<{
        customer: Company;
    }>) => {
        return dispatch => {
            // @ts-ignore
            return ajax.post("/online-venue/reservation/seller/edit-customer", data).then((r: {customer: Company}) => {
                // dispatch(BookingAction.setCustomer(r.customer));
                return r;
            });
        };
    };

    static fetchSetCustomer = (
        data: CompanyInputs,
        packagesCart: PackagesCart | null
    ): ((dispatch) => Promise<{
        customer: Company;
    }>) => {
        return dispatch => {
            // @ts-ignore
            return ajax.post("/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/set-customer", data).then((r: {customer: Company}) => {
                dispatch(BookingAction.setCustomer(r.customer));
                return r;
            });
        };
    };

    static fetchSetCustomerByCompanyId = (
        company_id: number,
        packagesCart: PackagesCart
    ): ((dispatch) => Promise<{
        customer: Company;
    }>) => {
        return dispatch => {
            // @ts-ignore
            return ajax
                .post("/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/set-customer-by-id", {company_id: company_id})
                .then((r: {customer: Company}) => {
                    dispatch(BookingAction.setCustomer(r.customer));
                    return r;
                });
        };
    };

    static fetchPayCart = (packagesCart: PackagesCart): ThunkAction<Promise<any>> => {
        return (dispatch, getState) => {
            const session = getState().sessionState.session;

            return ajax
                .post("/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/pay", {
                    return_url: location.protocol + "//" + location.host + (HAS_BASENAME ? "/" + DEFAULT_BASENAME : "") + "/booking/paid",
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                    },
                })
                .then((r: {redirect: string}) => {
                    window.location.href = r.redirect;
                    return r;
                });
        };
    };

    static fetchPaidGiftCardCart = (payment_id: number, paymentReference: string): ThunkAction<Promise<any>> => {
        return (dispatch, getState) => {
            const session = getState().sessionState.session;

            return ajax
                .get("/booking/cart/paid/giftcard/" + payment_id + "/" + paymentReference, {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${session?.access_token}`,
                    },
                })
                .then((r: {payment: Payment; assert: any}) => {
                    dispatch<Action<string, Payment | null>>(BookingAction.setPackagesCartPayment(r.payment));
                    dispatch<Action<string, PaymentAssert | null>>(BookingAction.setPackagesCartPaymentAssert(r.assert));
                    return r;
                });
        };
    };

    static fetchPaidCart = (payment_id: number, paymentReference: string): ThunkAction<Promise<any>> => {
        return (dispatch, getState) => {
            const session = getState().sessionState.session;

            return ajax
                .get("/booking/cart/paid/" + payment_id + "/" + paymentReference, {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${session && session.access_token}`,
                    },
                })
                .then((r: {payment: Payment; assert: any}) => {
                    dispatch<Action<string, Payment | null>>(BookingAction.setPackagesCartPayment(r.payment));
                    dispatch<Action<string, PaymentAssert | null>>(BookingAction.setPackagesCartPaymentAssert(r.assert));
                    return r;
                });
        };
    };

    static fetchPossibleTimeslots = (data: BookTimeslotParams, packagesCart: PackagesCart): ThunkAction<Promise<BookingPackagesTimeslotsResponse>> => {
        return dispatch => {
            return ajax.post("/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/book/possible-timeslots", data);
        };
    };

    static setPossibleTimeslots = (timeslots: Timeslot[]) => {
        return createAction(BookingAction.SET_POSSIBLE_TIMESLOTS, timeslots);
    };
    static setEmailVerified = (verified: boolean) => {
        return createAction(BookingAction.SET_EMAIL_VERIFIED, verified);
    };
    static setActivities = (activities: Activity[]): BookingReducerSetActivitiesAction => {
        return createAction(BookingAction.SET_ACTIVITIES, activities);
    };

    static setPackages = (packages: Package[]): BookingReducerSetPackagesAction => {
        return createAction(BookingAction.SET_PACKAGES, packages);
    };

    static setPackageItemsSelectedTimeslot = (timeslot: Timeslot) => {
        return createAction(BookingAction.SET_SELECTED_TIMESLOT, timeslot);
    };

    static setAvailablePackagesItems = (packagesItemsResponse: PackagesItemsResponse) => {
        return createAction(BookingAction.SET_AVAILABLE_PACKAGES_ITEMS, packagesItemsResponse);
    };

    static setSelectedPackageItems = (packagesItemsByPerson: PackagesItemByPerson[]) => {
        return createAction(BookingAction.SET_SELECTED_PACKAGE_ITEMS, packagesItemsByPerson);
    };
    static setSelectedPackageItem = (packagesItemsByPerson: PackagesItemByPerson) => {
        return createAction(BookingAction.SET_SELECTED_PACKAGE_ITEM, packagesItemsByPerson);
    };

    static resetSelectedPackageItems = () => {
        return createAction(BookingAction.RESET_SELECTED_PACKAGE_ITEMS, []);
    };

    static setTimeslots = (timeslots: BookingTimeslot[]): BookingReducerSetTimeslotsAction => {
        return createAction(BookingAction.SET_TIMESLOTS, timeslots);
    };
    static setTimeslotsAllowed = (timeslotsAllowed: boolean): BookingReducerSetTimeslotsAllowedAction => {
        return createAction(BookingAction.SET_TIMESLOTS_ALLOWED, timeslotsAllowed);
    };

    static setPackageCart = (packageCart: PackagesCart): BookingReducerSetPackageCartAction => {
        return createAction(BookingAction.SET_PACKAGE_CART, packageCart);
    };

    static setFilter = (data: BookingFilter): BookingReducerSetFilterAction => {
        return createAction(BookingAction.SET_FILTER, data);
    };

    static setFilterAdultsCount = (adults: number): BookingReducerSetFilterDrivesAdultCountAction => {
        return createAction(BookingAction.SET_FILTER_ADULTS_COUNT, adults);
    };
    static setFilterChildrenCount = (children: number): BookingReducerSetFilterDrivesChildCountAction => {
        return createAction(BookingAction.SET_FILTER_CHILDREN_COUNT, children);
    };
    static setDateChosenByCustomer = (chosen: boolean): BookingReducerSetDateChosenByCustomerAction => {
        return createAction(BookingAction.SET_DATE_CHOSEN_BY_CUSTOMER, chosen);
    };

    static setPackage = (data: Package | null): BookingReducerSetPackageAction => {
        return createAction(BookingAction.SET_PACKAGE, data);
    };

    static pushUpsell = (data: PackageUpsell): BookingReducerPushUpsellAction => {
        return createAction(BookingAction.PUSH_UPSELL, data);
    };

    static setUpsell = (data: PackageUpsell): BookingReducerSetUpsellAction => {
        return createAction(BookingAction.SET_UPSELL, data);
    };

    static removeUpsell = (data: PackageUpsell) => {
        return createAction(BookingAction.REMOVE_UPSELL, data);
    };

    static setCustomer = (data: Company): BookingReducerSetCustomerAction => {
        return createAction(BookingAction.SET_CUSTOMER, data);
    };

    static setCookies = cookies => {
        return createAction(BookingAction.SET_COOKIES, cookies);
    };

    static setPackagesCartPayment = (payment: Payment | null) => {
        return createAction(BookingAction.SET_PACKAGES_CART_PAYMENT, payment);
    };

    static setPackagesCartPaymentAssert = (paymentAssert: PaymentAssert | null) => {
        return createAction(BookingAction.SET_PACKAGES_CART_PAYMENT_ASSERT, paymentAssert);
    };

    static setCurrentNavigationStep = navigationStep => {
        return createAction(BookingAction.SET_CURRENT_NAVIGATION_STEP, navigationStep);
    };

    static clearBookingProcess = (payload: null = null) => {
        return createAction(BookingAction.CLEAR_BOOKING_PROCESS_STATE, payload);
    };

    static clearUpsells = (payload: [] = []) => {
        return createAction(BookingAction.CLEAR_UPSELLS, payload);
    };
    static setCustomTimeslot = (payload: Timeslot) => {
        return createAction(BookingAction.SET_CUSTOM_TIMESLOT, payload);
    };

    static resetCustomTimeslot = () => {
        return createAction(BookingAction.RESET_CUSTOM_TIMESLOTS, null);
    };

    static postInquiryMessage = (data: {
        name: string;
        last_name: string;
        email: string;
        phone: string;
        subject: string;
        message: string;
        hall_id: string;
    }): ThunkAction<Promise<Response>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax.post("/app/post-inquiry", data).then(r => {
                return r;
            });
        };
    };

    static fetchCreateInquiry = (packagesCart: PackagesCart): ThunkAction<Promise<any>> => {
        return dispatch => {
            return ajax.post("/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/inquiry", {}).then(r => {
                return r;
            });
        };
    };

    static confirmBookingByCustomer = (data): ThunkAction<Promise<any>> => {
        return dispatch => {
            return ajax
                .post("/booking/" + data.package_cart_guid + "/" + data.package_cart_token + "/confirm-by-customer", {
                    email: btoa(data.email),
                })
                .then(r => {
                    return r;
                });
        };
    };

    static confirmBookingBySeller = (packagesCart: PackagesCart, seller_id: number): ThunkAction<Promise<any>> => {
        return dispatch => {
            return ajax
                .post("/booking/" + packagesCart.guid + "/" + packagesCart.token + "/confirm-by-seller", {
                    seller_id: seller_id,
                })
                .then(r => {
                    return r;
                });
        };
    };

    static addUpsellsToReservationBySeller = (reservation_id: number, upsell: PackageUpsellItem[], seller_id: number): ThunkAction<Promise<any>> => {
        return dispatch => {
            return ajax
                .post("/online-venue/reservation/seller/add_upsells/" + reservation_id, {
                    upsell: upsell,
                    seller_id: seller_id,
                })
                .then(r => {
                    dispatch(BookingAction.clearUpsells());
                    return r;
                });
        };
    };

    static getReservationStatus = (package_cart_guid: string, package_cart_token: string): ThunkAction<Promise<any>> => {
        return dispatch => {
            return ajax.get("/booking/cart/" + package_cart_guid + "/" + package_cart_token + "/status").then(r => {
                return r;
            });
        };
    };

    static getReservationDrivers = (package_cart_guid: string, package_cart_token: string): ThunkAction<Promise<any>> => {
        return dispatch => {
            return ajax.get("/booking/cart/" + package_cart_guid + "/" + package_cart_token + "/drivers").then(r => {
                return r;
            });
        };
    };

    static setReservationDrivers = (data, package_cart_guid: string, package_cart_token: string): ThunkAction<Promise<any>> => {
        return dispatch => {
            return ajax.post("/booking/cart/" + package_cart_guid + "/" + package_cart_token + "/set-drivers", data).then(r => {
                return r;
            });
        };
    };
}

export default BookingAction;
